/**
 @license
 Copyright (c) 2015-2026 Lablup Inc. All rights reserved.
 */
import { Button, type ButtonProps, Tooltip } from 'antd';
import { CopyConfig } from 'antd/es/typography/Base';
import { CheckIcon, CopyIcon } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import { useTranslation } from 'react-i18next';

interface CopyButtonProps extends ButtonProps {
  copyable?: Omit<CopyConfig, 'text'> & { text: string };
  defaultIcon?: React.ReactNode;
}
const CopyButton: React.FC<CopyButtonProps> = ({
  copyable,
  defaultIcon,
  ...props
}) => {
  const [isCopied, setIsCopied] = useState(false);
  const { t } = useTranslation();

  const handleCopy = async () => {
    setIsCopied(true);
  };

  useEffect(() => {
    if (isCopied) {
      const timeout = setTimeout(() => {
        setIsCopied(false);
      }, 2000);
      return () => clearTimeout(timeout);
    }
  }, [isCopied]);

  return (
    <Tooltip
      title={
        props.disabled
          ? undefined
          : isCopied
            ? t('sourceCodeViewer.Copied')
            : t('sourceCodeViewer.Copy')
      }
      open={isCopied ? true : undefined}
    >
      <div>
        <CopyToClipboard text={copyable?.text || ''} onCopy={handleCopy}>
          <Button
            icon={
              isCopied ? (
                <CheckIcon />
              ) : defaultIcon ? (
                defaultIcon
              ) : (
                <CopyIcon />
              )
            }
            {...props}
          />
        </CopyToClipboard>
      </div>
    </Tooltip>
  );
};

export default CopyButton;
